export interface RentalPricingResult {
  price: number;
  price_per_sqm?: number;
  payment_frequency?: string;
  guarantee_type?: string;
  guarantee_months?: number;
  included_expenses?: string[];
  administration?: string;
  min_contract_months?: number;
}

interface FlatRentalForm {
  transaction_type?: string[];
  rental_price?: number;
  rental_payment_frequency?: string;
  rental_guarantee_type?: string;
  rental_guarantee_months?: number;
  rental_included_expenses?: string[];
  rental_min_contract_months?: number;
  rental_administration?: string;
  constructed_area?: number;
}

/**
 * Type guard for an already-nested rental pricing object (has a numeric
 * `price` and no flat `rental_price`).
 *
 * @param input - Value to inspect.
 * @returns True when the input is already a nested rental pricing object.
 */
function isAlreadyNested(input: unknown): input is RentalPricingResult {
  return (
    typeof input === "object" &&
    input !== null &&
    "price" in input &&
    typeof (input as Record<string, unknown>).price === "number" &&
    !("rental_price" in input)
  );
}

/**
 * Assign a value to a key only when it is neither undefined nor null, keeping
 * the output object free of empty optional fields.
 *
 * @param obj - Target object to mutate.
 * @param key - Destination key.
 * @param value - Candidate value; skipped when undefined or null.
 */
function addOptional<T>(
  obj: Record<string, unknown>,
  key: string,
  value: T | undefined | null,
): void {
  if (value !== undefined && value !== null) {
    obj[key] = value;
  }
}

/**
 * Whether the flat form describes a rental (has the Aluguel transaction type
 * and a rental price). Guards `buildRentalPricing` against non-rental input.
 *
 * @param form - Flat rental form values.
 * @returns True when a rental pricing object should be built.
 */
function shouldBuildRental(form: FlatRentalForm): boolean {
  const hasRentalTransaction = form.transaction_type?.includes("Aluguel");
  return Boolean(hasRentalTransaction && form.rental_price);
}

/**
 * Compute the rental price per square meter, or undefined when the area is
 * missing or non-positive.
 *
 * @param price - Monthly rental price.
 * @param area - Constructed area in square meters.
 * @returns Rounded price per square meter, or undefined.
 */
function pricePerSqm(price: number, area: number | undefined): number | undefined {
  if (area && area > 0) {
    return Math.round(price / area);
  }
  return undefined;
}

/**
 * Normalize a list to undefined when it is empty, so empty lists are not
 * written to the nested pricing object.
 *
 * @param list - Optional list of strings.
 * @returns The list when non-empty, otherwise undefined.
 */
function nonEmptyList(list: string[] | undefined): string[] | undefined {
  return list?.length ? list : undefined;
}

/**
 * Build the nested rental pricing object from flat form values, passing
 * through an already-nested input unchanged.
 *
 * @param input - Flat rental form, an already-nested pricing object, or record.
 * @returns The nested rental pricing object, or undefined when not a rental.
 */
export function buildRentalPricing(
  input: FlatRentalForm | RentalPricingResult | Record<string, unknown>,
): RentalPricingResult | undefined {
  if (isAlreadyNested(input)) {
    return input;
  }

  const form = input as FlatRentalForm;
  if (!shouldBuildRental(form)) {
    return undefined;
  }

  const price = form.rental_price as number;
  const result: Record<string, unknown> = { price };

  addOptional(result, "price_per_sqm", pricePerSqm(price, form.constructed_area));
  addOptional(result, "payment_frequency", form.rental_payment_frequency);
  addOptional(result, "guarantee_type", form.rental_guarantee_type);
  addOptional(result, "guarantee_months", form.rental_guarantee_months);
  addOptional(result, "included_expenses", nonEmptyList(form.rental_included_expenses));
  addOptional(result, "min_contract_months", form.rental_min_contract_months);
  addOptional(result, "administration", form.rental_administration);

  return result as unknown as RentalPricingResult;
}
